import type { ICreateOrUpdateTranslationCmd } from "@/data/commands/system/CreateOrUpdateTranslationCmd";
import { Factory } from "@/data/dao/Factory";
import { SystemDao } from "@/data/dao/service/SystemDao";
import { Language } from "@/data/model/Enums";
import type { ITranslation } from "@/data/model/System";

import { BaseModal } from "@/shared/elements/BaseModal";
import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";

import { i18n } from "@/portal/Trans";
import { MultiLanguageInput } from "@/portal/formControls/MultiLanguageInput";

export class TranslationWindow extends BaseModal {
    private $form: JQuery;
    private $keyField: JQuery;
    private textField: MultiLanguageInput;
    private $submitBtn: JQuery;

    private locationId: string;
    private model: ITranslation;

    public onSaved?: { (translation: ITranslation) };
    public onRemoved?: { () };

    constructor(locationId: string, model?: ITranslation) {
        super(
            model === undefined ? i18n("modal.translation.addTitle") : i18n("modal.translation.editTitle"),
            "modal-lg",
            true
        );

        this.locationId = locationId;
        this.model = model!;
        this.$element.addClass("translation");

        this.$form = $('<form class="kt-form kt-form--label-right" />').appendTo(this.$body);
        const createSection = $('<div class="kt-section kt-section--first"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");
        createSection.append(
            '<div class="row"><label class="col-xl-4"></label><div class="col-lg-9 col-xl-6"><h3 class="kt-section__title kt-section__title-sm">' +
                i18n("modal.translation.systemText") +
                "</h3></div></div>"
        );
        let container = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.translation.key") +
                '</label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        this.$keyField = $(
            '<input type="text" name="key" class="form-control" placeholder="tableSelectionPopover.welcomeTo" value="' +
                (this.model === undefined ? "" : this.model.key) +
                '" />'
        ).appendTo(container);
        if (this.model !== undefined) this.$keyField.prop("readonly", true);

        container = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.translation.text") +
                '</label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        this.textField = new MultiLanguageInput().renderTo(container);

        if (this.model !== undefined) {
            this.textField
                .setValue(this.model.valueDe, Language.German)
                .setValue(this.model.valueEn, Language.English)
                .setValue(this.model.valueFr, Language.French)
                .setValue(this.model.valueIt, Language.Italian);
        }

        if (this.model !== undefined) {
            const deleteBtn = $('<button class="btn btn-clean btn-small">' + i18n("general.delete") + "</button>")
                .appendTo(this.$footer!)
                .click((e) => {
                    e.preventDefault();

                    deleteBtn
                        .addClass("kt-spinner kt-spinner--left kt-spinner--sm kt-spinner--primary disabled")
                        .prop("disabled", true);
                    Factory.getInstance<SystemDao>(SystemDao)
                        .deleteTranslation(this.locationId, this.model.id)
                        .then(() => {
                            this.close();
                            if (this.onRemoved !== undefined) this.onRemoved();
                        })
                        .catch((ex) => {
                            logError(ex);
                            deleteBtn
                                .removeClass("kt-spinner kt-spinner--left kt-spinner--sm kt-spinner--primary disabled")
                                .prop("disabled", false);
                            alert(i18n("modal.translation.failedDelete"));
                        });
                });
        }

        $('<button type="button" class="btn btn-secondary">' + i18n("general.cancel") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.close();
            });

        this.$submitBtn = $('<button type="button" class="btn btn-success">' + i18n("general.save") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.$form.submit();
            });

        this.$form.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onSubmit();
            return false;
        });
    }

    private onSubmit() {
        const cmd: ICreateOrUpdateTranslationCmd = {
            key: this.$keyField.val().length === 0 ? undefined : this.$keyField.val(),
            valueDe: this.textField.getValue(Language.German),
            valueEn: this.textField.getValue(Language.English),
            valueIt: this.textField.getValue(Language.Italian),
            valueFr: this.textField.getValue(Language.French),
            locationId: this.locationId,
        };

        let hasError = false;
        this.$form.find(".is-invalid").removeClass("is-invalid");
        if (cmd.key === undefined) {
            this.$keyField.addClass("is-invalid");
            hasError = true;
        }

        if (
            cmd.valueDe === undefined ||
            cmd.valueEn === undefined ||
            cmd.valueFr === undefined ||
            cmd.valueIt === undefined
        ) {
            hasError = true;
            this.textField.setError(true);
        } else this.textField.setError(false);

        if (hasError) return;

        this.$submitBtn.addClass(style.buttonLoaderClass).prop("disabled", true).find("> .la").remove();
        Factory.getInstance<SystemDao>(SystemDao)
            .createOrUpdateTranslation(cmd)
            .then((m) => {
                this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                if (this.$submitBtn.find("> .la").length === 0) this.$submitBtn.prepend('<i class="la la-save" />');

                this.close();
                if (this.onSaved !== undefined) this.onSaved(m);
            })
            .catch((ex) => {
                logError(ex);
                this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                if (this.$submitBtn.find("> .la").length === 0) this.$submitBtn.prepend('<i class="la la-save" />');

                alert(i18n("modal.translation.failedEdit"));
            });
    }
}
